import _ from "lodash";
const { forEach, isEqual, map, pick } = _;

const booleanOperatorFnMap = {
  AND: (value1, value2) => value1 && value2,
  OR: (value1, value2) => value1 || value2,
};

const comparativeOperatorFnMap = {
  isEqual,
  isNotEqual: (value1, value2) => !isEqual(value1, value2),
  contains: (value1, value2) =>
    !!value1 && !!value1.length && value1.includes(value2),
};

export default {
  evaluateConditional(
    acc,
    { attribute, booleanOperator, comparativeOperator, value: conditionValue },
    entity
  ) {
    const comparativeOperatorFn = comparativeOperatorFnMap[comparativeOperator];

    // If first condition in list there will not be a boolean operator
    if (!booleanOperator) {
      return comparativeOperatorFn(entity[attribute], conditionValue);
    }

    const booleanOperatorFn = booleanOperatorFnMap[booleanOperator];

    return booleanOperatorFn(
      acc,
      comparativeOperatorFn(entity[attribute], conditionValue)
    );
  },
  evaluateConditionalGroup(acc, { booleanOperator, list }, entity) {
    const listAcc = list.reduce((acc, condition) => {
      const { type } = condition;

      // If condition group, recurse until you reach a condition
      if (type === "conditionalGroup") {
        return this.evaluateConditionalGroup(acc, condition, entity);
      }

      return this.evaluateConditional(acc, condition, entity);
    }, acc);

    // If first conditionGroup in list, there will not be a boolean operator so just return the value
    if (!booleanOperator) {
      return listAcc;
    }

    const booleanOperatorFn = booleanOperatorFnMap[booleanOperator];

    return booleanOperatorFn(acc, listAcc);
  },
  resolveAction({ actionId, conditionalList, defaultValue = false, entity }) {
    if (!Array.isArray(conditionalList)) {
      return defaultValue;
    }

    const conditionalActionList = conditionalList.filter(({ then }) => {
      if (!Array.isArray(then)) {
        return false;
      }

      return then.map(({ id }) => id).includes(actionId);
    });

    if (!conditionalActionList.length) {
      return defaultValue;
    }

    const firstPassingConditional = conditionalActionList.find(conditional => {
      return this.resolveConditional({
        conditional,
        entity,
      });
    });

    if (!firstPassingConditional) {
      return false;
    }

    return firstPassingConditional.then.find(({ id }) => id === actionId);
  },
  resolveConditional({ conditional, entity }) {
    const { if: conditionalIf } = conditional;
    const { type } = conditionalIf;

    if (type === "conditionalGroup") {
      return this.evaluateConditionalGroup(true, conditionalIf, entity);
    }

    return this.evaluateConditional(true, conditionalIf, entity);
  },

  // Returns a list of PASSING conditional actions, keyed by attribute name.
  // ex:
  // {
  //   firstName: [{ setValue }, { show }],
  //   lastName: [{require}],
  // }
  resolveEntity({ attributeList, entity }) {
    if (typeof entity !== "object" || !Object.keys(entity).length) {
      return entity;
    }
    if (!Array.isArray(attributeList) || !attributeList.length) {
      return entity;
    }

    let conditionalActionListByName = {};
    for (const attribute of attributeList) {
      const { name, conditionalList } = attribute;
      conditionalActionListByName[name] = [];

      if (!conditionalList || !name) {
        continue;
      }

      conditionalList.forEach(conditional => {
        const conditionalSuccess = this.resolveConditional({
          conditional,
          entity,
        });

        if (conditionalSuccess) {
          // Append conditionalThen's contents if success
          conditionalActionListByName[name] = conditionalActionListByName[
            name
          ].concat(conditional.then);
        }

        if (
          // If no success AND conditionalThen contains a "show", Append "hide".
          !conditionalSuccess &&
          Array.isArray(conditional.then) &&
          conditional.then.find(({ id }) => id === "show")
        ) {
          conditionalActionListByName[name] = conditionalActionListByName[
            name
          ].concat(["hide"]);
        }
      });
    }

    /* At this point, conditionalActionListByName looks similar to this:
        {
          "providerNpiNumber": [
            "hide"
          ],
          "testAttr": [
            {
              "id": "setValue",
              "label": "Set Value",
              "valueDefault": "10"
            }
          ],
          "title": [
            "show"
          ],
          "typeOfStaff": [
            "require"
          ]
        }
    */

    const visibleAttributeNameList = map(
      conditionalActionListByName,
      (actionList, attributeName) => {
        if (!actionList.includes("hide")) {
          return attributeName;
        }
      }
    );

    // Breaking the reference chain to remain a "Pure" function,
    // And only contains attributes that are shown as per conditionals.
    let resolvedEntity = pick(entity, visibleAttributeNameList);
    forEach(conditionalActionListByName, (actionList, attributeName) => {
      const setValue = actionList.find(({ id }) => id === "setValue");

      if (setValue && setValue.valueDefault !== undefined) {
        resolvedEntity[attributeName] = setValue.valueDefault;
      }
    });

    return resolvedEntity;
  },
};
